import api, { getAccessToken } from './api';
import qs from 'qs';

export interface CreateCompetitionConfig {
  competitionId: string,
  type: 'EQUAL' | 'BETWEEN' | 'GREATER_THAN_EQUAL',
  firstPlace: number,
  lastPlace?: number
}

export interface CreateRewardsConfig {
  kind: 'RANDOM_BOOSTER' | 'RANDOM_STICKER' | 'COINS' | 'ENERGY' | 'RANDOM_PRIZE_BOX' | 'SHOP_ITEM',
  type: 'QUANTITY' | 'PERCENTS',
  value: number,
  itemId?: string | undefined,
  placeId: string
}

export interface UpdateCompetitionConfig {
  id: string,
  type: 'EQUAL' | 'BETWEEN' | 'GREATER_THAN_EQUAL',
  firstPlace: number,
  lastPlace?: number
}

export interface UpdateRewardsConfig {
  kind: 'RANDOM_BOOSTER' | 'RANDOM_STICKER' | 'COINS' | 'ENERGY' | 'RANDOM_PRIZE_BOX' | 'SHOP_ITEM',
  type: 'QUANTITY' | 'PERCENTS',
  value: number,
  itemId: string | undefined,
  id: string
}

export const getChallengeSettings = async () => {
  await getAccessToken();
  
  const settings = await api.get('app-settings');
  
  return settings.data;
}

export const setChallengeSettings = async (challengeSettings: any) => {
  await getAccessToken();
  
  const updatedSettings = challengeSettings.map((item: any) => {
    return {
      ...(item?.id && {id: item.id}),
      key: item.key,
      data: {
        ...item.data,
        energyPrice: Number(item.data.energyPrice),
        ...(item.data.minutesToAddOneEnergy && { minutesToAddOneEnergy: Number(item.data.minutesToAddOneEnergy) })
      }
    }
  })

  const settings = await api.put('app-settings', { data: updatedSettings });
  return settings.data;
}

export const getAvailableRegions = async () => {
  await getAccessToken();
  
  const regionList = await api.get('regions');
  
  return regionList.data;
}

export const updateRegion = async (id: string, newName: string) => {
  await getAccessToken();
  
  const updatedRegion = await api.put(`regions/${id}`, { data: { name: newName } });
  
  return updatedRegion.data;
}

export const createNewRegion = async (name: string) => {
  await getAccessToken();
  
  const newRegion = await api.post('regions', { data: { name } });
  
  return newRegion.data;
}

export const deleteRegion = async (id: string) => {
  await getAccessToken();
  
  await api.deleteApi(`regions/${id}`);
}

export const getAvailableGroups = async (props?: any) => {
  const groupsList = await api.get(`groups?${qs.stringify(props)}`);
  
  return groupsList.data;
}

export const createNewGroup = async (config: any) => {
  const groupsList = await api.post('groups', { data: config });
  
  return groupsList.data;
}

export const updateGroup = async (config: any, groupId: string) => {
  const groupsList = await api.put(`groups/${groupId}`, { data: config });
  
  return groupsList.data;
}

export const deleteGroup = async (groupId: string) => {
  await api.deleteApi(`groups/${groupId}`);
}

export const getLevels = async () => {
  const levelsList = await api.get('levels');

  return levelsList.data;
}

export const createNewLevel = async (config: { name: string; xpGoal: number }) => {
  const levelsList = await api.post('levels', { data: config });
  
  return levelsList.data;
}

export const updateLevel = async (config: { name: string; xpGoal: number }) => {
  const levelsList = await api.put('levels', { data: config });
  
  return levelsList.data;
}

export const deleteLevel = async (levelId: string) => {
  const levelsList = await api.deleteApi(`levels/${levelId}`);
  
  return levelsList.data;
}

export const getCompetitionPlaces = async (competitionId: string) => {
  const competitionPlaces = await api.get(`competition-places?${qs.stringify({ competitionId })}`);
  
  return competitionPlaces.data;
}

export const createCompetitionPlace = async (config: CreateCompetitionConfig) => {
  const competitionPlace = await api.post('competition-places', { data: config });
  
  return competitionPlace.data;
}

export const updateCompetitionPlace = async (config: UpdateCompetitionConfig) => {
  const updatedConfig = {
    type: config.type,
    firstPlace: config.firstPlace,
    lastPlace: config.lastPlace || 0
  };
  
  const competitionPlace = await api.put(`competition-places/${config.id}`, { data: updatedConfig });
  
  return competitionPlace.data;
}

export const deleteCompetitionPlace = async (placeId: string) => {
  const deletedCompetitionPlace = await api.deleteApi(`competition-places/${placeId}`);
  
  return deletedCompetitionPlace.data;
}

export const getShopItems = async () => {
  const shopItemsList = await api.get(`shop/items?${qs.stringify({ take: 100 })}`);
  
  return shopItemsList.data;
}

export const createNewRewards = async (config: CreateRewardsConfig) => {
  const updatedConfig = {
    kind: config.kind,
    type: config.type,
    value: config.value,
    ...(config.itemId && { itemId: config.itemId }),
    placeId: config.placeId
  };
  const rewards = await api.post('competition-rewards', { data: updatedConfig });
  
  return rewards.data;
}

export const updateReward = async (config: UpdateRewardsConfig) => {
  const updatedConfig = {
    kind: config.kind,
    type: config.type,
    value: config.value,
    itemId: config.itemId
  };
  const updatedRewards = await api.put(`competition-rewards/${config.id}`, { data: updatedConfig });
  
  return updatedRewards.data;
};

export const deleteReward = async (rewardId: string) => {
  const deletedCompetitionReward = await api.deleteApi(`competition-rewards/${rewardId}`);
  
  return deletedCompetitionReward.data;
}